import { useAuth0 } from "@auth0/auth0-react";
import { useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  ENTERPRISE_SALES_INQUIRY_MODAL_SEARCH_PARAM_KEY,
  ENTERPRISE_SALES_INQUIRY_MODAL_SEARCH_PARAM_VALUE,
  EnterpriseSalesInquiryModal,
} from "components/enterprise/EnterpriseSalesInquiryModal";
import { Section, SectionHeader } from "components/landing/layout/Section";
import { PricingCard } from "components/landing/pricing/PricingCard";
import { TooltipProvider } from "components/products/muggleTestV0/components/ui/tooltip";
import { ENTERPRISE_SALES_SUBMISSION_SOURCE } from "constants/enterpriseSalesInquiryConfig";
import { PRICING_FOOTNOTES, PRICING_PLANS } from "content/landing";
import { useLandingAuthActions } from "hooks/landing/useLandingAuthActions";
import { type CtaLocation, useSectionViewTracking } from "lib/analytics";
import type { PricingPlan } from "types/landing/landing.types";
import { SubscriptionPlanEnum } from "types/subscription/SubscriptionPlanEnum";

const PLAN_CTA_LOCATIONS: Record<string, CtaLocation> = {
  Free: "pricing_free",
  Starter: "pricing_starter",
  Pro: "pricing_pro",
  Enterprise: "pricing_enterprise",
};

export function PricingSection() {
  const { isAuthenticated } = useAuth0();
  const { signup, signupWithPlan, buildReturnTo } = useLandingAuthActions();
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [, setSearchParams] = useSearchParams();
  useSectionViewTracking("pricing", sectionRef);

  /** Handle signup CTA click with plan-aware routing. */
  const handleSignupClick = (plan: PricingPlan) => {
    const location = PLAN_CTA_LOCATIONS[plan.name];
    const tierId = plan.tierId;

    // Free tier or no tierId: use existing signup flow (lands on dashboard root)
    if (!tierId || tierId === SubscriptionPlanEnum.FREE) {
      void signup(location);
      return;
    }

    // Authenticated user: skip Auth0 and navigate directly to billing with plan param
    if (isAuthenticated) {
      navigate(buildReturnTo(tierId));
      return;
    }

    // Unauthenticated user with paid tier: Auth0 signup with plan-aware returnTo
    void signupWithPlan({ tierId: tierId, location: location });
  };

  return (
    <Section id="pricing" className="bg-muted/20 py-4 sm:py-8 lg:py-16">
      <div ref={sectionRef} className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div data-landing-scroll-anchor>
          <SectionHeader
            title="Pricing"
            description="Start free, then scale run capacity and team controls as you grow."
          />
        </div>

        <TooltipProvider delayDuration={200}>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {PRICING_PLANS.map((plan) => (
              <PricingCard
                key={plan.name}
                plan={plan}
                onContactSalesClick={() => {
                  setSearchParams(
                    (prev) => {
                      const next = new URLSearchParams(prev);
                      next.set(ENTERPRISE_SALES_INQUIRY_MODAL_SEARCH_PARAM_KEY, ENTERPRISE_SALES_INQUIRY_MODAL_SEARCH_PARAM_VALUE);
                      return next;
                    },
                    { replace: true }
                  );
                }}
                onSignupClick={handleSignupClick}
              />
            ))}
          </div>
        </TooltipProvider>

        <EnterpriseSalesInquiryModal submissionSourceLabel={ENTERPRISE_SALES_SUBMISSION_SOURCE.MARKETING_PRICING} />

        <div className="mx-auto mt-10 max-w-2xl border-t border-border/40 pt-5">
          <p className="mb-1 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground/50">
            How pricing works
          </p>
          {PRICING_FOOTNOTES.map((note) => (
            <p key={note} className="mt-2 text-center text-xs leading-relaxed text-muted-foreground/65">
              {note}
            </p>
          ))}
        </div>
      </div>
    </Section>
  );
}
